import {
    Dropdown,
    Option,
    OptionGroup,
    type OptionOnSelectData,
    type SelectionEvents,
} from '@fluentui/react-components';
import type { Column } from '@tanstack/react-table';
import { type Dispatch, type SetStateAction, useCallback } from 'react';
import { useSharedStyles } from '../../shared-styles.js';
import { typedSetNumberColumnFilterValue } from '../filter/index.js';
import { NUMBER_FILTER_OPERATION, type NumberFilterValuesWithoutInArray } from '../filter/operation.js';
import type { NumberFilterSchemaWithoutInArray } from '../filter/schema.js';
import { DROPDOWN_NUMBER_OPTIONS } from './data.js';

type NumberOperationProps<TData, TValue> = {
    column: Column<TData, TValue>;
    dropdownValue: NumberFilterValuesWithoutInArray['id'];
    resetListFilter: (resetDropdown: boolean) => void;
    setDropdownValue: Dispatch<SetStateAction<NumberFilterValuesWithoutInArray['id']>>;
};

export function NumberOperation<TData, TValue>({
    column,
    dropdownValue,
    resetListFilter,
    setDropdownValue,
}: NumberOperationProps<TData, TValue>) {
    const sharedStyles = useSharedStyles();

    const onDropdownChange = useCallback(
        (_: SelectionEvents, changeData: OptionOnSelectData) => {
            const operation = changeData.selectedOptions[0] as typeof dropdownValue;

            if (
                dropdownValue === (NUMBER_FILTER_OPERATION.inArray.id as NumberFilterValuesWithoutInArray['id']) &&
                operation !== (NUMBER_FILTER_OPERATION.inArray.id as NumberFilterValuesWithoutInArray['id'])
            ) {
                resetListFilter(false);
            }

            setDropdownValue(operation);

            typedSetNumberColumnFilterValue(column, {
                ...(column.getFilterValue() as NumberFilterSchemaWithoutInArray | undefined),
                operation,
            });
        },
        [column, dropdownValue, resetListFilter, setDropdownValue]
    );

    return (
        <Dropdown
            className={sharedStyles.dropdown}
            value={NUMBER_FILTER_OPERATION[dropdownValue].label}
            selectedOptions={[dropdownValue]}
            onOptionSelect={onDropdownChange}
            listbox={{
                className: sharedStyles.dropdownListbox,
            }}
        >
            {DROPDOWN_NUMBER_OPTIONS.map((optionGroup, index) => (
                <OptionGroup key={index}>
                    {optionGroup.map((option) => {
                        return (
                            <Option key={option} value={option} text={NUMBER_FILTER_OPERATION[option].label}>
                                {NUMBER_FILTER_OPERATION[option].label}
                            </Option>
                        );
                    })}
                </OptionGroup>
            ))}
        </Dropdown>
    );
}
